import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  API_RATE_LIMIT,
  HTTP_STATS_CODE,
  LOCAL_STORAGE,
  TIMEOUT_DELAY
} from 'constants/general';
import {
  AXIOS_ERROR_CODE,
  AXIOS_TIMEOUT_CODES,
  DOM_EXCEPTION_NAME
} from 'constants/axios-error-codes';
import { POST_HOG_EVENT } from 'constants/events';
import { BASE_URL, ROUTES } from 'constants/routes';
import { t } from 'services/i18n.service';
import { capturePostHogEvent } from 'services/posthog.service';
import { toast } from 'react-toastify';
import { getLocalStorage, removeLocalStorage } from 'utils/helpers/storage';
import { isRouteEmbedded } from 'utils/routes.util';
import { v4 as uuidv4 } from 'uuid';

import { refreshAccessToken } from './auth.service';
import {
  isEmbeddedNativeContext,
  redirectEmbeddedToErrorPage,
  requestAccessTokenFromNative
} from 'bridge/native-auth.bridge';
import type { AppDispatch } from 'store';
import {
  clearApiRateLimitedUntil,
  setApiRateLimitedUntil,
  setHasDataLoadFailed,
  setIsOffline
} from 'store/reducer/general/slice';
import { getAppDispatch } from 'store/store-access';
import {
  clearApiRateLimitBlockedUntil,
  createApiRateLimitedError,
  getApiRateLimitBlockedUntil,
  isApiRateLimited,
  parseRetryAfterMs,
  setApiRateLimitBlockedUntil
} from 'utils/api-rate-limit.util';
import { NATIVE_TOKEN_REQUEST_REASON } from 'enums';
import { createSingleFlight } from 'utils/single-flight.util';
import { isAccessTokenValid } from 'utils/auth-token-validation.util';
import { isTransientRequestError } from 'utils/api-error.util';
import { isRefreshTokenRejected } from 'utils/auth-refresh.util';
import { RetriableRequestConfig } from 'interfaces/auth.interface';
import { isSupportPanelPage } from 'utils/support-panel.util';

const toBearerAuthorization = (token: string): string => `Bearer ${token}`;

const setRequestBearerToken = (
  config: InternalAxiosRequestConfig | RetriableRequestConfig,
  token: string
): void => {
  config.headers = config.headers ?? {};
  config.headers.Authorization = toBearerAuthorization(token);
};

const focusBearApi = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT_DELAY.AXIOS,
  headers: {
    accept: '*/*',
    'x-context-id': uuidv4()
  }
});

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

let rateLimitDispatch: AppDispatch | null = null;
let apiRateLimitedUntilRef: number | null = null;

export const bindApiRateLimitDispatch = (dispatch: AppDispatch) => {
  rateLimitDispatch = dispatch;
  const stored = getApiRateLimitBlockedUntil();
  if (stored && isApiRateLimited(stored)) {
    apiRateLimitedUntilRef = stored;
    dispatch(setApiRateLimitedUntil(stored));
  } else if (stored) {
    clearApiRateLimitBlockedUntil();
  }
};

export const resetApiRateLimitState = () => {
  apiRateLimitedUntilRef = null;
  clearApiRateLimitBlockedUntil();
  rateLimitDispatch?.(clearApiRateLimitedUntil());
};

let hasRedirectedToForbidden = false;

export const resetForbiddenRedirectState = (): void => {
  hasRedirectedToForbidden = false;
};

const redirectToForbiddenPage = (): void => {
  if (hasRedirectedToForbidden) {
    return;
  }
  if (window.location.pathname === ROUTES.FORBIDDEN || isSupportPanelPage()) {
    return;
  }
  hasRedirectedToForbidden = true;
  window.location.pathname = ROUTES.FORBIDDEN;
};

const sanitiseUrl = (url?: string) => {
  if (!url) return 'unknown';
  return url.split('?')[0];
};

const isTimeoutError = (error: AxiosError) =>
  !!error.code && AXIOS_TIMEOUT_CODES.includes(error.code);

const logApiFailureToPostHog = (error: AxiosError) => {
  const config = error.config as RetriableRequestConfig;

  if (error.code === AXIOS_ERROR_CODE.REQUEST_CANCELED) {
    return;
  }

  // Skip initial 401 before retry to avoid noisy false failures
  if (
    error.response?.status === HTTP_STATS_CODE.UNAUTHORIZED &&
    !config?._retry
  ) {
    return;
  }

  // Same for the initial 402: it may only mean the token's entitlement
  // claims are stale, so wait for the post-refresh retry before logging.
  if (
    error.response?.status === HTTP_STATS_CODE.PAYMENT_REQUIRED &&
    !config?._entitlementRetry
  ) {
    return;
  }

  const properties = {
    endpoint_url: sanitiseUrl(config?.url),
    status_code: error.response?.status ?? null,
    method: config?.method?.toUpperCase() ?? 'UNKNOWN',
    timeout_ms: config?.timeout ?? null,
    error_code: error.code ?? null,
    error_message: error.message
  };

  if (isTimeoutError(error)) {
    capturePostHogEvent(POST_HOG_EVENT.BACKEND.TIMED_OUT, properties);
    return;
  }

  capturePostHogEvent(POST_HOG_EVENT.BACKEND.ERRORED, properties);
};

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const isAbortLike = (error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return true;
  }
  if (
    error instanceof DOMException &&
    error.name === DOM_EXCEPTION_NAME.ABORT
  ) {
    return true;
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === DOM_EXCEPTION_NAME.ABORT
  );
};

const shouldPreserveSessionAfterRefreshFailure = (
  refreshError?: unknown
): boolean => {
  if (refreshError && isRefreshTokenRejected(refreshError)) {
    return false;
  }

  if (refreshError && isTransientRequestError(refreshError)) {
    return true;
  }

  if (!isRouteEmbedded && !isEmbeddedNativeContext()) {
    return false;
  }

  const accessToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
  return isAccessTokenValid(accessToken);
};

const handleTransientRefreshFailure = (error: AxiosError): void => {
  processQueue(error, null);
  isRefreshing = false;

  // The session is preserved on transient failures, so the request rejects back
  // to the caller without an error page. Surface a shared "refresh" affordance
  // so the user isn't left with a misleading empty state.
  getAppDispatch()?.(setHasDataLoadFailed(true));
};

const handleRefreshTokenError = (
  error: AxiosError,
  refreshError?: unknown,
  skipSessionPreservation = false
) => {
  if (
    !skipSessionPreservation &&
    shouldPreserveSessionAfterRefreshFailure(refreshError)
  ) {
    handleTransientRefreshFailure(error);
    return;
  }

  processQueue(error, null);
  isRefreshing = false;
  resetApiRateLimitState();
  removeLocalStorage([
    LOCAL_STORAGE.AUTH_BEARER_TOKEN,
    LOCAL_STORAGE.AUTH_BEARER_REFRESH,
    LOCAL_STORAGE.AUTH_ID
  ]);

  if (isRouteEmbedded) {
    redirectEmbeddedToErrorPage();
    return;
  }

  if (isSupportPanelPage()) {
    // Tokens are cleared, so reloading in place shows the panel's own sign-in.
    window.location.reload();
    return;
  }

  window.location.pathname = ROUTES.LOGIN;
  toast.info(t('auth_page.looks_like_your_session_has_timed_out'));
};

export const tryNativeTokenRefresh = async (): Promise<boolean> => {
  if (!isEmbeddedNativeContext()) {
    return false;
  }

  if (getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_REFRESH)) {
    return false;
  }

  return requestAccessTokenFromNative(NATIVE_TOKEN_REQUEST_REASON.UNAUTHORIZED);
};

/**
 * The backend reads entitlement claims (e.g. `activeEntitlements`) from inside
 * the Auth0 access token, so after a plan/entitlement change the SPA keeps
 * 402-ing until the JWT is re-minted. Force one fresh token and retry.
 * Single-flight so a burst of simultaneous 402s shares one refresh.
 */
const refreshTokenForEntitlements = createSingleFlight(
  async (): Promise<boolean> => {
    const nativeRefreshed = await tryNativeTokenRefresh();
    return nativeRefreshed || refreshAccessToken();
  }
);

focusBearApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (isApiRateLimited(apiRateLimitedUntilRef)) {
      return Promise.reject(createApiRateLimitedError());
    }

    const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);

    if (token) {
      setRequestBearerToken(config, token);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

focusBearApi.interceptors.response.use(
  (response) => {
    // A successful request means connectivity is back — clear the offline
    // indicator. `hasDataLoadFailed` is intentionally NOT cleared here: the
    // data on screen is stale and only a full page refresh (via the refresh
    // affordance) fixes it, so the flag stays sticky until the user refreshes.
    getAppDispatch()?.(setIsOffline(false));
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig;

    if (
      error.response?.status === HTTP_STATS_CODE.UNAUTHORIZED &&
      !originalRequest?._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (typeof token === 'string') {
              setRequestBearerToken(originalRequest, token);
            }

            return focusBearApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const nativeRefreshed = await tryNativeTokenRefresh();

        if (nativeRefreshed) {
          const newToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);

          processQueue(null, newToken);

          if (newToken) {
            setRequestBearerToken(originalRequest, newToken);
          }

          isRefreshing = false;
          return focusBearApi(originalRequest);
        }

        const hadRefreshToken = !!getLocalStorage(
          LOCAL_STORAGE.AUTH_BEARER_REFRESH
        );
        const refreshed = await refreshAccessToken();

        if (refreshed) {
          const newToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);

          processQueue(null, newToken);

          if (newToken) {
            setRequestBearerToken(originalRequest, newToken);
          }

          isRefreshing = false;
          return focusBearApi(originalRequest);
        }

        const wasRefreshTokenRejected =
          hadRefreshToken &&
          !getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_REFRESH);

        if (wasRefreshTokenRejected) {
          logApiFailureToPostHog(error);
          handleRefreshTokenError(error, undefined, true);
          return Promise.reject(error);
        }

        if (shouldPreserveSessionAfterRefreshFailure()) {
          handleTransientRefreshFailure(error);
          return Promise.reject(error);
        }

        logApiFailureToPostHog(error);
        handleRefreshTokenError(error);
        return Promise.reject(error);
      } catch (refreshError) {
        const axiosRefreshError = refreshError as AxiosError;
        if (shouldPreserveSessionAfterRefreshFailure(refreshError)) {
          handleTransientRefreshFailure(error);
          return Promise.reject(error);
        }
        logApiFailureToPostHog(axiosRefreshError);
        handleRefreshTokenError(axiosRefreshError, refreshError);
        return Promise.reject(refreshError);
      }
    }

    if (
      error.response?.status === HTTP_STATS_CODE.PAYMENT_REQUIRED &&
      !originalRequest?._entitlementRetry
    ) {
      // Retry once with a re-minted token; a second 402 falls through to the
      // caller's normal error handling (genuinely missing entitlement).
      originalRequest._entitlementRetry = true;

      try {
        const refreshed = await refreshTokenForEntitlements();

        if (refreshed) {
          const newToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);

          if (newToken) {
            setRequestBearerToken(originalRequest, newToken);
          }

          return focusBearApi(originalRequest);
        }
      } catch {
        // Refresh failure is non-fatal here — surface the original 402 below.
      }

      logApiFailureToPostHog(error);
      return Promise.reject(error);
    }

    if (error.response?.status === HTTP_STATS_CODE.TOO_MANY_REQUEST) {
      const cooldownMs = parseRetryAfterMs(error.response.headers);

      if (cooldownMs !== null) {
        const until = Date.now() + cooldownMs;
        apiRateLimitedUntilRef = until;
        setApiRateLimitBlockedUntil(until);
        rateLimitDispatch?.(setApiRateLimitedUntil(until));
      }

      toast.warning(t(API_RATE_LIMIT.I18N.TOAST), {
        toastId: API_RATE_LIMIT.TOAST_ID
      });
      return Promise.reject(error);
    }

    // Permission denial (authenticated but not allowed). Keep the session and
    // send the user to Forbidden once — first 403 wins for parallel failures.
    if (error.response?.status === HTTP_STATS_CODE.FORBIDDEN) {
      logApiFailureToPostHog(error);
      redirectToForbiddenPage();
      return Promise.reject(error);
    }

    // A network-level failure (no response) means the user is likely offline.
    // Track it globally so the shared refresh affordance can show the offline
    // state; a later successful request clears it. Also set the sticky
    // `hasDataLoadFailed` so the affordance stays visible after reconnect —
    // the data on screen is stale and only a user-triggered refresh fixes it.
    if (isTransientRequestError(error)) {
      getAppDispatch()?.(setIsOffline(true));
      getAppDispatch()?.(setHasDataLoadFailed(true));
    }

    logApiFailureToPostHog(error);
    return Promise.reject(error);
  }
);

export default focusBearApi;

export const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};
